import { getFromServerTopic } from "@utility/slice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING } from "@constants";
import {
  formatDate,
  formatDateHourMinutes,
  ONE_WEEK_RANGE,
} from "@helpers/utils.js";
import { getIntervalsForDateRange } from "@store/appdata.js";
import moment from "moment";
import { MILLIS_PER_DAY, MILLIS_PER_SECOND } from "@helpers/time.js";

const SLICE = "modeling";

const action = (name) => `${SLICE}/${name}`;

export const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, contextOptionsFn, payloadFn });

const getExpectedProfile = getFromServer(
  "getExpectedProfile",
  ({ arg }) => arg,
);

const getActualProfile = createAsyncThunk(
  action("getActualProfile"),
  async ({ datapointId, dateRange }, { dispatch }) => {
    const options = {
      fetchKey: "modelingDebugActuals",
      id: [datapointId],
      startdate: formatDate(dateRange[0]),
      stopdate: formatDate(dateRange[1]),
    };
    const result = await dispatch(
      getIntervalsForDateRange({
        options,
      }),
    );
    return result.payload.response.payload;
  },
);

const getBuildingReportingYears = getFromServerTopic({
  topic: `buildingReporting`,
  action: "getBuildingReportingYears",
  contextOptionsFn: ({ arg }) => arg,
  payloadFn: (arg) => arg,
});

const getDatapoint = (datapoints, matchFn) => {
  return datapoints.find(matchFn);
};

export const initializePage = createAsyncThunk(
  action("initializePage"),
  async ({ datapointId, siteId }, { getState, dispatch }) => {
    const { appData } = getState();
    const datapoints = appData.buildingData[siteId].datapoints.EIMS;
    const datapoint = getDatapoint(
      datapoints,
      (it) => it.datapoint_id === datapointId,
    );

    await dispatch(getBuildingReportingYears({ siteId }));

    const { baselineYear, dateRange } =
      getState().modeling.buildingData[siteId];

    const expectedProfile = dispatch(
      getExpectedProfile({
        siteId,
        baselineYear,
        datapointId,
        isDebug: true,
        startDate: formatDate(dateRange[0]),
        endDate: formatDate(dateRange[1]),
      }),
    );

    const actualProfile = dispatch(
      getActualProfile({ datapointId, dateRange, siteId }),
    );

    const profiles = await Promise.all([expectedProfile, actualProfile]);

    return {
      datapoint,
      datapoints,
      profiles,
    };
  },
);

export const setBaselineYear = createAsyncThunk(
  action("setBaselineYear"),
  async ({ baselineYear, siteId }, { getState, dispatch }) => {
    const { datapointId, dateRange } = getState().modeling.buildingData[siteId];
    const result = await dispatch(
      getExpectedProfile({
        siteId,
        baselineYear,
        datapointId,
        isDebug: true,
        startDate: formatDate(dateRange[0]),
        endDate: formatDate(dateRange[1]),
      }),
    );
    return result.payload;
  },
);

export const setDateRange = createAsyncThunk(
  action("setDateRange"),
  async ({ dateRange, siteId }, { getState, dispatch }) => {
    const { baselineYear, datapointId } =
      getState().modeling.buildingData[siteId];
    const [expectedProfile, actualProfile] = await Promise.all([
      dispatch(
        getExpectedProfile({
          siteId,
          baselineYear,
          datapointId,
          isDebug: true,
          startDate: formatDate(dateRange[0]),
          endDate: formatDate(dateRange[1]),
        }),
      ),
      dispatch(getActualProfile({ datapointId, dateRange, siteId })),
    ]);
    return { expected: expectedProfile.payload, actual: actualProfile.payload };
  },
);

export const setDatapoint = createAsyncThunk(
  action("setDatapoint"),
  async (payload, { getState, dispatch }) => {
    const { datapoint, siteId } = payload;
    const { baselineYear, dateRange } =
      getState().modeling.buildingData[siteId];
    const datapointId = datapoint.datapoint_id;
    const [expectedProfile, actualProfile] = await Promise.all([
      dispatch(
        getExpectedProfile({
          siteId,
          baselineYear,
          datapointId,
          isDebug: true,
          startDate: formatDate(dateRange[0]),
          endDate: formatDate(dateRange[1]),
        }),
      ),
      dispatch(
        getActualProfile({
          datapointId,
          dateRange,
          siteId,
        }),
      ),
    ]);
    return { expected: expectedProfile.payload, actual: actualProfile.payload };
  },
);

export const slice = createSlice({
  name: SLICE,
  initialState: {
    buildingData: {},
  },
  reducers: {
    setPointDebugInfo: (state, action) => {
      const { siteId, pointDebugInfo } = action.payload;

      state.buildingData[siteId] = {
        ...state.buildingData[siteId],
        pointDebugInfo,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActualProfile.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { siteId } = meta.arg;

        const actualProfile = {};
        for (const { date, intervals } of payload) {
          const baseDate = moment(date).unix() * 1000;
          const intervalSec =
            MILLIS_PER_DAY / MILLIS_PER_SECOND / intervals.length;
          intervals?.map((value, idx) => {
            const offset = idx * ((intervalSec || 300) * 1000);
            actualProfile[formatDateHourMinutes(new Date(baseDate + offset))] =
              { value };
          });
        }

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          actualProfile,
        };
      })
      .addCase(getExpectedProfile.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { siteId } = meta.arg;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          expectedProfile: payload.response,
        };
      })
      .addCase(getBuildingReportingYears.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { siteId } = meta.arg;

        const sortedYears = [...payload.response.years];
        sortedYears.sort().reverse();

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          baselineYear: new Date().getFullYear() - 1,
          years: sortedYears,
        };
      })
      .addCase(initializePage.pending, (state, action) => {
        const { meta } = action;
        const { datapointId, siteId } = meta.arg;

        state.buildingData[siteId] = {
          status: LOADING,
          datapointId,
          dateRange: ONE_WEEK_RANGE,
        };
      })
      .addCase(initializePage.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { siteId } = meta.arg;
        const { datapoint, datapoints } = payload;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          datapoint,
          datapoints,
          status: IDLE,
        };
      })
      .addCase(setBaselineYear.fulfilled, (state, action) => {
        const { meta } = action;
        const { siteId, baselineYear } = meta.arg;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          status: IDLE,
          baselineYear,
        };
      })
      .addCase(setDatapoint.pending, (state, action) => {
        const { meta } = action;
        const { siteId } = meta.arg;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          status: LOADING,
        };
      })
      .addCase(setDatapoint.fulfilled, (state, action) => {
        const { meta } = action;
        const { datapoint, siteId } = meta.arg;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          datapoint,
          status: IDLE,
        };
      })
      .addCase(setDateRange.pending, (state, action) => {
        const { meta } = action;
        const { siteId } = meta.arg;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          status: LOADING,
        };
      })
      .addCase(setDateRange.fulfilled, (state, action) => {
        const { meta } = action;
        const { siteId, dateRange } = meta.arg;

        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          status: IDLE,
          dateRange,
        };
      });
  },
});

export const { setPointDebugInfo } = slice.actions;

export default slice.reducer;
